/**
 * Properties Panel
 * Edit properties of selected nodes and edges
 */

import { memo, useState, useEffect } from 'react'
import { Node, Edge } from '@xyflow/react'

interface PropertiesPanelProps {
  selectedNodes: Node[]
  selectedEdges: Edge[]
  onNodeUpdate: (nodeId: string, updates: Partial<Node>) => void
  onEdgeUpdate: (edgeId: string, updates: Partial<Edge>) => void
  onClose?: () => void
}

function PropertiesPanel({
  selectedNodes,
  selectedEdges,
  onNodeUpdate,
  onEdgeUpdate,
  onClose,
}: PropertiesPanelProps) {
  const [activeTab, setActiveTab] = useState<'node' | 'edge'>('node')
  const selectedNode = selectedNodes[0]
  const selectedEdge = selectedEdges[0]

  // Auto-switch tabs
  useEffect(() => {
    if (selectedNodes.length > 0) {
      setActiveTab('node')
    } else if (selectedEdges.length > 0) {
      setActiveTab('edge')
    }
  }, [selectedNodes.length, selectedEdges.length])

  if (selectedNodes.length === 0 && selectedEdges.length === 0) {
    return (
      <div className="properties-panel">
        <div className="properties-header">
          <h3>Properties</h3>
          {onClose && (
            <button onClick={onClose} className="close-btn">
              ×
            </button>
          )}
        </div>
        <div className="properties-empty">
          <p>Select a node or edge to edit properties</p>
        </div>
      </div>
    )
  }

  return (
    <div className="properties-panel">
      
      <div className="properties-header">
        <h3>Properties</h3>
        {onClose && (
          <button onClick={onClose} className="close-btn">
            ×
          </button>
        )}
      </div>

      
      {selectedNodes.length > 0 && selectedEdges.length > 0 && (
        <div className="properties-tabs">
          <button
            className={activeTab === 'node' ? 'active' : ''}
            onClick={() => setActiveTab('node')}
          >
            Node ({selectedNodes.length})
          </button>
          <button
            className={activeTab === 'edge' ? 'active' : ''}
            onClick={() => setActiveTab('edge')}
          >
            Edge ({selectedEdges.length})
          </button>
        </div>
      )}

      
      <div className="properties-content">
        {activeTab === 'node' && selectedNode && (
          <NodeProperties node={selectedNode} onUpdate={onNodeUpdate} />
        )}
        {activeTab === 'edge' && selectedEdge && (
          <EdgeProperties edge={selectedEdge} onUpdate={onEdgeUpdate} />
        )}
      </div>
    </div>
  )
}

// ============================================================================
// NODE PROPERTIES
// ============================================================================

function NodeProperties({
  node,
  onUpdate,
}: {
  node: Node
  onUpdate: (nodeId: string, updates: Partial<Node>) => void
}): JSX.Element {
  const isShapeNode = node.type === 'shape' || Boolean(node.data?.isShape)

  const [label, setLabel] = useState((node.data?.label as string) || '')
  const [icon, setIcon] = useState((node.data?.icon as string) || '')
  const [description, setDescription] = useState((node.data?.description as string) || '')
  const [bgColor, setBgColor] = useState((node.data?.bgColor as string) || (isShapeNode ? '#000000' : '#e3f2fd'))
  const [borderColor, setBorderColor] = useState((node.data?.borderColor as string) || (isShapeNode ? '#000000' : '#2563eb'))
  const [borderWidth, setBorderWidth] = useState((node.data?.borderWidth as number) || 2)
  const [fillOpacity, setFillOpacity] = useState((node.data?.fillOpacity as number) !== undefined ? (node.data.fillOpacity as number) : 1)

  // Size & Position controls
  const [minWidth, setMinWidth] = useState((node.data?.minWidth as number) || 25)
  const [minHeight, setMinHeight] = useState((node.data?.minHeight as number) || 25)
  const [maxWidth, setMaxWidth] = useState((node.data?.maxWidth as number) || 1000)
  const [maxHeight, setMaxHeight] = useState((node.data?.maxHeight as number) || 1000)
  const [sizePositionExpanded, setSizePositionExpanded] = useState(false)

  const handleUpdate = () => {
    onUpdate(node.id, {
      data: {
        ...node.data,
        label,
        icon,
        description,
        bgColor,
        borderColor,
        borderWidth,
        fillOpacity,
        minWidth,
        minHeight,
        maxWidth,
        maxHeight,
        style: {
          ...(node.data?.style as any || {}),
          bgColor,
          borderColor,
        },
      },
    })
  }

  return (
    <div className="node-properties">
      <div className="property-group">
        <label>Label</label>
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          onBlur={handleUpdate}
          placeholder="Node label"
        />
      </div>

      {/* Only show icon field for non-shape nodes */}
      {!isShapeNode ? (
        <div className="property-group">
          <label>Icon (Emoji)</label>
          <input
            type="text"
            value={icon}
            onChange={(e) => setIcon(e.target.value)}
            onBlur={handleUpdate}
            placeholder="⚛️"
            maxLength={2}
          />
        </div>
      ) : null}

      {/* Show shape info for shape nodes */}
      {isShapeNode && node.data?.shapeId ? (
        <div className="property-group">
          <label>Shape</label>
          <div className="property-value">{String(node.data.shapeId)}</div>
        </div>
      ) : null}

      <div className="property-group">
        <label>Description</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          onBlur={handleUpdate}
          placeholder="Optional description..."
          rows={3}
        />
      </div>

      <div className="property-group">
        <label>Background Color</label>
        <div className="color-input">
          <input
            type="color"
            value={bgColor}
            onChange={(e) => setBgColor(e.target.value)}
            onBlur={handleUpdate}
          />
          <input
            type="text"
            value={bgColor}
            onChange={(e) => setBgColor(e.target.value)}
            onBlur={handleUpdate}
            placeholder="#e3f2fd"
          />
        </div>
      </div>

      <div className="property-group">
        <label>Border Color</label>
        <div className="color-input">
          <input
            type="color"
            value={borderColor}
            onChange={(e) => setBorderColor(e.target.value)}
            onBlur={handleUpdate}
          />
          <input
            type="text"
            value={borderColor}
            onChange={(e) => setBorderColor(e.target.value)}
            onBlur={handleUpdate}
            placeholder="#2563eb"
          />
        </div>
      </div>

      {/* Shape-specific controls */}
      {isShapeNode ? (
        <>
          <div className="property-group">
            <label>Border Width</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="range"
                min="0"
                max="10"
                step="1"
                value={borderWidth}
                onChange={(e) => {
                  setBorderWidth(Number(e.target.value))
                  handleUpdate()
                }}
                style={{ flex: 1 }}
              />
              <span style={{ minWidth: '40px' }}>{borderWidth}px</span>
            </div>
          </div>

          <div className="property-group">
            <label>Fill Opacity</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={fillOpacity}
                onChange={(e) => {
                  setFillOpacity(Number(e.target.value))
                  handleUpdate()
                }}
                style={{ flex: 1 }}
              />
              <span style={{ minWidth: '40px' }}>{Math.round(fillOpacity * 100)}%</span>
            </div>
          </div>
        </>
      ) : null}

      {/* Size & Position Section */}
      <div className="property-group">
        <button
          onClick={() => setSizePositionExpanded(!sizePositionExpanded)}
          style={{
            width: '100%',
            padding: '8px',
            background: '#f3f4f6',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontWeight: '500',
          }}
        >
          <span>Size & Position</span>
          <span>{sizePositionExpanded ? '▼' : '▶'}</span>
        </button>
      </div>

      {sizePositionExpanded && (
        <>
          {/* Current Size (Read-only) */}
          <div className="property-group">
            <label>Current Size</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '11px', color: '#666', marginBottom: '2px' }}>Width</div>
                <input
                  type="text"
                  value={`${Math.round(node.measured?.width || node.width || 120)}px`}
                  readOnly
                  style={{ background: '#f9fafb', color: '#6b7280' }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '11px', color: '#666', marginBottom: '2px' }}>Height</div>
                <input
                  type="text"
                  value={`${Math.round(node.measured?.height || node.height || 120)}px`}
                  readOnly
                  style={{ background: '#f9fafb', color: '#6b7280' }}
                />
              </div>
            </div>
          </div>

          {/* Current Position (Read-only) */}
          <div className="property-group">
            <label>Position</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '11px', color: '#666', marginBottom: '2px' }}>X</div>
                <input
                  type="text"
                  value={`${Math.round(node.position.x)}px`}
                  readOnly
                  style={{ background: '#f9fafb', color: '#6b7280' }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '11px', color: '#666', marginBottom: '2px' }}>Y</div>
                <input
                  type="text"
                  value={`${Math.round(node.position.y)}px`}
                  readOnly
                  style={{ background: '#f9fafb', color: '#6b7280' }}
                />
              </div>
            </div>
          </div>

          {/* Min Width & Height */}
          <div className="property-group">
            <label>Minimum Size</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '11px', color: '#666', marginBottom: '2px' }}>Min Width</div>
                <input
                  type="number"
                  value={minWidth}
                  onChange={(e) => setMinWidth(Number(e.target.value))}
                  onBlur={handleUpdate}
                  min={10}
                  max={maxWidth}
                  placeholder="25"
                />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '11px', color: '#666', marginBottom: '2px' }}>Min Height</div>
                <input
                  type="number"
                  value={minHeight}
                  onChange={(e) => setMinHeight(Number(e.target.value))}
                  onBlur={handleUpdate}
                  min={10}
                  max={maxHeight}
                  placeholder="25"
                />
              </div>
            </div>
          </div>

          {/* Max Width & Height */}
          <div className="property-group">
            <label>Maximum Size</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '11px', color: '#666', marginBottom: '2px' }}>Max Width</div>
                <input
                  type="number"
                  value={maxWidth}
                  onChange={(e) => setMaxWidth(Number(e.target.value))}
                  onBlur={handleUpdate}
                  min={minWidth}
                  max={5000}
                  placeholder="1000"
                />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '11px', color: '#666', marginBottom: '2px' }}>Max Height</div>
                <input
                  type="number"
                  value={maxHeight}
                  onChange={(e) => setMaxHeight(Number(e.target.value))}
                  onBlur={handleUpdate}
                  min={minHeight}
                  max={5000}
                  placeholder="1000"
                />
              </div>
            </div>
          </div>
        </>
      )}

      <div className="property-group">
        <label>Node Type</label>
        <div className="property-value">{String(node.data?.nodeType || 'N/A')}</div>
      </div>

      {node.data?.subtype ? (
        <div className="property-group">
          <label>Subtype</label>
          <div className="property-value">{String(node.data.subtype)}</div>
        </div>
      ) : null}
    </div>
  )
}

// ============================================================================
// EDGE PROPERTIES
// ============================================================================

function EdgeProperties({
  edge,
  onUpdate,
}: {
  edge: Edge
  onUpdate: (edgeId: string, updates: Partial<Edge>) => void
}) {
  const [label, setLabel] = useState((edge.label as string) || '')
  const [protocol, setProtocol] = useState((edge.data?.protocol as string) || '')
  const [strokeColor, setStrokeColor] = useState((edge.style?.stroke as string) || '#2563eb')
  const [strokeWidth, setStrokeWidth] = useState((edge.style?.strokeWidth as number) || 2)
  const [animated, setAnimated] = useState(edge.animated || false)

  const handleUpdate = () => {
    onUpdate(edge.id, {
      label,
      animated,
      data: {
        ...edge.data,
        protocol,
      },
      style: {
        ...edge.style,
        stroke: strokeColor,
        strokeWidth,
      },
    })
  }

  return (
    <div className="edge-properties">
      
      <div className="property-group">
        <label>Label</label>
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          onBlur={handleUpdate}
          placeholder="Edge label"
        />
      </div>

      
      <div className="property-group">
        <label>Protocol</label>
        <input
          type="text"
          value={protocol}
          onChange={(e) => setProtocol(e.target.value)}
          onBlur={handleUpdate}
          placeholder="HTTP, gRPC, etc."
        />
      </div>

      
      <div className="property-group">
        <label>Line Color</label>
        <div className="color-input">
          <input
            type="color"
            value={strokeColor}
            onChange={(e) => setStrokeColor(e.target.value)}
            onBlur={handleUpdate}
          />
          <input
            type="text"
            value={strokeColor}
            onChange={(e) => setStrokeColor(e.target.value)}
            onBlur={handleUpdate}
            placeholder="#2563eb"
          />
        </div>
      </div>

      
      <div className="property-group">
        <label>Line Width</label>
        <input
          type="range"
          min="1"
          max="10"
          value={strokeWidth}
          onChange={(e) => {
            setStrokeWidth(Number(e.target.value))
            handleUpdate()
          }}
        />
        <span>{strokeWidth}px</span>
      </div>

      
      <div className="property-group">
        <label>
          <input
            type="checkbox"
            checked={animated}
            onChange={(e) => {
              setAnimated(e.target.checked)
              onUpdate(edge.id, { animated: e.target.checked })
            }}
          />
          Animated
        </label>
      </div>

      
      <div className="property-group">
        <label>Edge Type</label>
        <div className="property-value">{String((edge.data?.edgeType as string) || edge.type || 'N/A')}</div>
      </div>
    </div>
  )
}

export default memo(PropertiesPanel)
